/**
 * 拍照流程 · 侧边栏红点 slice（PRD 3.3）
 *
 * 侧边栏「拍照流程」菜单项上那颗纯红点（不带数字）的唯一状态源：
 * 只要还有任意一张表有未查看的新任务，就亮红点。
 *
 * 数据从哪来：复用进入团队时已经发出的 /form/list（见 taskFlowLandingSlice 的
 * fetchTaskFlowLandingState），监听它的 fulfilled 取 unreadFormIDs —— 红点不额外发请求。
 * 接口文档里 /form/list 的 startTime/endTime 只影响 taskStats 计数，hasNewTask 按
 * 用户自己的未读水位算、不受统计区间约束，所以那次「不限时间」的请求拿到的红点是准的。
 *
 * 谁来清：用户进拍照流程页、逐个流程卡看过去，TaskFlowDashboard 把当前未读流程集合
 * （useFlowRedDots 的 unreadFlowIds，已含本地 ack 的即时收敛）同步进来。全部看完 →
 * 集合空 → 菜单红点消失；之后再有新提交，下次拉 /form/list 时红点重新亮（可反复）。
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchTaskFlowLandingState } from './taskFlowLandingSlice'
import type { TaskFlowListSnapshot } from './taskFlowLandingSlice'

interface TaskFlowRedDotState {
  /** 当前红点所属团队 realID；切团队时用来丢弃迟到的 stale 结果 */
  groupID: string
  /** 有未查看新任务的表单 id；非空即亮红点 */
  unreadFormIDs: number[]
}

const initialState: TaskFlowRedDotState = {
  groupID: '',
  unreadFormIDs: []
}

/** syncTaskFlowRedDots 的入参 */
interface SyncTaskFlowRedDotsPayload {
  /** 团队 realID */
  groupID: string
  /** 该团队当前仍未查看的表单 id 全集（不是增量） */
  formIDs: number[]
}

const taskFlowRedDotSlice = createSlice({
  name: 'taskFlowRedDot',
  initialState,
  reducers: {
    /**
     * 用页面侧的实时未读集合覆盖红点状态（全量覆盖，不是增量）。
     *
     * 拍照流程页在用的 useFlowRedDots 已经处理了「服务端 ack 成功但 /form/list 还没重刷」
     * 的时间窗，页面上红点灭了的流程这里也应立刻灭 —— 故按它的结果整体覆盖，
     * 而不是各清各的，免得两处判定不一致。
     */
    syncTaskFlowRedDots: (
      state,
      action: PayloadAction<SyncTaskFlowRedDotsPayload>
    ) => {
      // 页面还停留在上一个团队的数据时不要污染当前团队的红点
      if (state.groupID && action.payload.groupID !== state.groupID) {
        return
      }
      state.groupID = action.payload.groupID
      state.unreadFormIDs = action.payload.formIDs
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskFlowLandingState.pending, (state, action) => {
        // 切团队瞬间先把归属改过去，让上一个团队迟到的 fulfilled 能被识别成 stale
        state.groupID = action.meta.arg.groupID
        state.unreadFormIDs = []
      })
      .addCase(
        fetchTaskFlowLandingState.fulfilled,
        (state, action: PayloadAction<TaskFlowListSnapshot>) => {
          if (action.payload.groupID !== state.groupID) return
          state.unreadFormIDs = action.payload.unreadFormIDs
        }
      )
    // rejected 不处理：拉不到就维持「不亮红点」，宁可漏提示也别凭空亮一颗点不掉的点
  }
})

export const { syncTaskFlowRedDots } = taskFlowRedDotSlice.actions
export default taskFlowRedDotSlice.reducer
